import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useNavigate } from "react-router";
import { v4 as uuidv4 } from "uuid";
import { useQuery } from "@rocicorp/zero/react";

import { registerUser, unregisterUser } from "@/api/rooms";
import {
  RoomConnectionHandler,
  type RoomMessage,
  WebsocketRoomTransport,
} from "@/api/rooms.ws";
import { DURABLE_OBJECT_HOST } from "@/env";
import { useLogin } from "@/hooks/use-login";
import { useZero } from "@/hooks/use-zero";
import { DocumentQueryRepository } from "@/zero/document.repo";
import { type RenderedDocument } from "@/blocks/blocks";
import { BlockTree } from "@/blocks/block-tree";

interface Cursor {
  userId: string;
  longitude: number;
  latitude: number;
  viewBounds: {
    north: number;
    south: number;
    east: number;
    west: number;
  };
}

interface RoomContextType {
  currentRoom: string | undefined;
  currentDocument: string | undefined;
  currentBlockTree: BlockTree | undefined;
  setCurrentDocument: (documentId: string | undefined) => void;
  joinRoom: () => void;
  leaveRoom: () => void;
  showRoomInfo: () => void;
  setMyCursor: (
    longitude: number,
    latitude: number,
    viewBounds: { north: number; south: number; east: number; west: number },
  ) => void;
  setJoinedRoom: (roomId: string) => void;
  cursors: Map<string, Cursor>;
  docs: DocumentQueryRepository | undefined;
}

const RoomContext = createContext<RoomContextType | undefined>(undefined);

export function RoomProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useLogin();
  const z = useZero();
  const [currentRoom, setCurrentRoom] = useState<string | undefined>(undefined);
  const [currentDocument, setCurrentDocument] = useState<string | undefined>(
    undefined,
  );
  const [cursors, setCursors] = useState<Map<string, Cursor>>(new Map());
  const clientId = uuidv4();

  const docs = useMemo(() => {
    if (!z) return undefined;
    return new DocumentQueryRepository(z);
  }, [z]);

  const [doc] = useQuery(
    currentDocument && docs
      ? docs.getDocumentById(currentDocument).one()
      : (z?.query.document.where("id", "=", "__never__").one() ?? null),
  );

  const currentBlockTree = useMemo(() => {
    if (!doc || !("blocks" in doc)) return undefined;
    return new BlockTree(doc as RenderedDocument);
  }, [doc]);

  const url = new URL(DURABLE_OBJECT_HOST);

  const handleCursorUpdate = (message: RoomMessage): void => {
    if (message.type === "cursor_update") {
      setCursors((prevCursors: Map<string, Cursor>) => {
        if (message.userId == user?.id && message.clientId == clientId)
          return prevCursors;
        const updatedCursors = new Map(prevCursors);
        updatedCursors.set(message.clientId, {
          longitude: message.cursor.longitude,
          latitude: message.cursor.latitude,
          viewBounds: message.cursor.viewBounds,
          userId: message.userId,
        });
        return updatedCursors;
      });
    } else if (message.type === "cursor_leave") {
      setCursors((prevCursors: Map<string, Cursor>) => {
        const updatedCursors = new Map(prevCursors);
        updatedCursors.delete(message.clientId);
        return updatedCursors;
      });
    }
  };

  const roomHandler = useMemo(() => {
    if (!isAuthenticated || !currentRoom || !user) return;
    const transport = new WebsocketRoomTransport(
      url.host,
      window.location.protocol === "https:",
    );
    const handler = new RoomConnectionHandler(
      transport,
      user.id,
      clientId,
      currentRoom,
    );
    handler.setMessageHandler(handleCursorUpdate);
    handler.connect();
    // TODO: error handlers
    return handler;
  }, [currentRoom, isAuthenticated, user]);

  useEffect(() => {
    const savedRoom = localStorage.getItem("currentRoom");
    if (savedRoom) {
      setCurrentRoom(savedRoom);
    }
  }, []);

  const joinRoom = useCallback(() => {
    navigate("/rooms");
  }, [navigate]);

  const setJoinedRoom = useCallback(
    async (roomId: string) => {
      await registerUser(roomId, user?.id || "anon");
      setCurrentRoom(roomId);
      localStorage.setItem("currentRoom", roomId);
    },
    [user],
  );

  const leaveRoom = useCallback(async () => {
    // Unregister from current room if we're in one
    if (currentRoom && user?.id) {
      try {
        unregisterUser(currentRoom, user.id);
      } catch (error) {
        console.error("Failed to unregister user:", error);
      }
      if (roomHandler) {
        roomHandler.leaveCursor();
        roomHandler.disconnect();
      }
    }

    // Clear room state
    setCurrentRoom(undefined);
    localStorage.removeItem("currentRoom");
    navigate("/");
  }, [currentRoom, user, navigate]);

  const showRoomInfo = useCallback(() => {
    if (currentRoom) {
      navigate(`/rooms/${currentRoom}`);
    }
  }, [currentRoom, navigate]);

  const value = {
    currentRoom,
    joinRoom,
    leaveRoom,
    showRoomInfo,
    setJoinedRoom,
    cursors,
    currentDocument,
    currentBlockTree,
    setCurrentDocument,
    docs,
    setMyCursor: (
      longitude: number,
      latitude: number,
      viewBounds: { north: number; south: number; east: number; west: number },
    ) => {
      if (roomHandler) {
        roomHandler.updateCursor(longitude, latitude, viewBounds);
      }
    },
  };

  return <RoomContext.Provider value={value}>{children}</RoomContext.Provider>;
}

export function useRoom() {
  const context = useContext(RoomContext);
  if (context === undefined) {
    throw new Error("useRoom must be used within a RoomProvider");
  }
  return context;
}
